/**
 * WhatsAppRescheduleDialog (06/2026, Task C) — „Termin per WhatsApp verschieben"
 * Admin öffnet den Dialog für einen Termin, sieht ALLE freien Slots des Tages,
 * wählt 1–n aus und sendet dem Kunden die Meta-Vorlage. Der Kunde antwortet
 * per WhatsApp mit einer Uhrzeit → das System bucht automatisch um.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { MessageCircle, Loader2, Send, CheckCircle2, CalendarClock, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppRescheduleDialog = ({ open, onClose, appointmentId, onSent }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [info, setInfo] = useState(null);
  const [selected, setSelected] = useState({}); // key = slot idx
  const [result, setResult] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [mode, setMode] = useState('day'); // 'day' | 'week'

  const authHeaders = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  const load = useCallback(async (dateArg, modeArg) => {
    if (!appointmentId) return;
    const m = modeArg || 'day';
    setLoading(true);
    setResult(null);
    setSelected({});
    try {
      const params = { appointment_id: appointmentId };
      if (m === 'week') params.days = 7;
      else if (dateArg) params.date = dateArg;
      const res = await axios.get(`${API}/whatsapp/reschedule/slots`, { ...authHeaders, params });
      setInfo(res.data);
      setSelectedDate(res.data?.target_date || dateArg || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Slots konnten nicht geladen werden');
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    if (open) { setMode('day'); load(); }
    else { setInfo(null); setSelected({}); setResult(null); setSelectedDate(''); setMode('day'); }
  }, [open, load]);

  const onDateChange = (e) => {
    const d = e.target.value;
    setSelectedDate(d);
    load(d, 'day');
  };

  const switchMode = (m) => {
    setMode(m);
    load(m === 'day' ? selectedDate : null, m);
  };

  const toggle = (idx) => setSelected((p) => ({ ...p, [idx]: !p[idx] }));

  const chosenSlots = (info?.slots || []).filter((_, i) => selected[i]);

  const WD = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  const dayLabel = (isoDate) => {
    const d = new Date(`${isoDate}T00:00:00`);
    if (isNaN(d)) return isoDate;
    return `${WD[d.getDay()]}, ${isoDate.split('-').reverse().slice(0, 2).join('.')}.`;
  };
  const groupedByDay = (() => {
    const map = new Map();
    (info?.slots || []).forEach((s, i) => {
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date).push({ s, i });
    });
    return Array.from(map.entries());
  })();
  const renderSlot = (s, i) => (
    <button
      key={i}
      onClick={() => toggle(i)}
      data-testid={`wa-reschedule-slot-${s.local_time}`}
      className={`rounded-lg border px-2 py-2 text-sm text-left transition-colors ${
        selected[i]
          ? 'bg-emerald-500 text-white border-emerald-500'
          : 'bg-white hover:bg-emerald-50 border-gray-200'
      }`}
    >
      <div className="font-semibold">{s.local_time}</div>
      <div className={`text-[11px] truncate ${selected[i] ? 'text-emerald-50' : 'text-gray-400'}`}>
        {s.trainer_name || ''}
      </div>
    </button>
  );

  const send = async () => {
    if (chosenSlots.length === 0) {
      toast.error('Bitte mindestens eine Uhrzeit auswählen');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/whatsapp/reschedule/offer`, {
        appointment_id: appointmentId,
        slots: chosenSlots.map((s) => ({
          local_time: s.local_time, date: s.date, datetime: s.datetime,
          end_datetime: s.end_datetime, trainer_id: s.trainer_id, trainer_name: s.trainer_name,
        })),
      }, authHeaders);
      setResult(res.data);
      if (res.data?.status === 'preview_blocked') {
        toast.warning('Preview: Versand blockiert (nur Testnummern). Angebot gespeichert.');
      } else {
        toast.success('Verschiebe-Angebot per WhatsApp gesendet 🎉');
      }
      onSent?.();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (detail.includes('does not exist') || detail.includes('translation') || detail.includes('send_failed')) {
        toast.error('Die WhatsApp-Vorlage ist noch nicht von Meta genehmigt. Bitte später erneut versuchen.');
      } else if (detail.includes('customer_phone_missing')) {
        toast.error('Für diesen Kunden ist keine Telefonnummer hinterlegt.');
      } else if (detail.includes('already_billed')) {
        toast.error('Dieser Termin ist bereits abgerechnet.');
      } else {
        toast.error(detail || 'Angebot konnte nicht gesendet werden');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="wa-reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle size={18} className="text-emerald-600" />
            Termin per WhatsApp verschieben
          </DialogTitle>
          <DialogDescription>
            Wähle freie Zeiten aus. Der Kunde bekommt sie per WhatsApp und muss
            nur mit der gewünschten Uhrzeit antworten – dann buchen wir automatisch um.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-500">
            <Loader2 className="animate-spin mr-2" size={18} /> Lädt freie Slots…
          </div>
        ) : result ? (
          <div className="py-6 text-center space-y-3" data-testid="wa-reschedule-result">
            <CheckCircle2 className="mx-auto text-emerald-500" size={40} />
            <p className="font-medium">
              {result.status === 'preview_blocked'
                ? 'Angebot gespeichert (Preview: Versand blockiert).'
                : 'Angebot wurde per WhatsApp gesendet.'}
            </p>
            <p className="text-sm text-gray-500">
              Sobald {info?.customer_name || 'der Kunde'} mit einer Uhrzeit antwortet,
              wird der Termin automatisch umgebucht.
            </p>
            <Button variant="outline" onClick={onClose} data-testid="wa-reschedule-close">Schliessen</Button>
          </div>
        ) : info ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-gray-50 border p-3 text-sm space-y-1">
              <div className="flex items-center gap-2"><User size={14} className="text-gray-400" />
                <span className="font-medium">{info.customer_name || 'Unbekannt'}</span>
                {info.customer_phone && <span className="text-gray-400">· {info.customer_phone}</span>}
              </div>
              <div className="flex items-center gap-2"><CalendarClock size={14} className="text-gray-400" />
                <span>Aktuell: {info.current_display}</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="text-sm font-medium">
                  Freie Zeiten ({(info.slots || []).length})
                </div>
                <div className="flex items-center gap-2">
                  {/* Tag/Woche Umschalter */}
                  <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs">
                    <button
                      onClick={() => switchMode('day')}
                      className={`px-2 py-1 ${mode === 'day' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600'}`}
                      data-testid="wa-reschedule-mode-day"
                    >Tag</button>
                    <button
                      onClick={() => switchMode('week')}
                      className={`px-2 py-1 ${mode === 'week' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600'}`}
                      data-testid="wa-reschedule-mode-week"
                    >Woche</button>
                  </div>
                  {mode === 'day' && (
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={onDateChange}
                      className="rounded-md border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      data-testid="wa-reschedule-date"
                    />
                  )}
                </div>
              </div>
              {(info.slots || []).length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center" data-testid="wa-reschedule-noslots">
                  {mode === 'week' ? 'Keine freien Slots in dieser Woche gefunden.' : 'Keine freien Slots an diesem Tag gefunden.'}
                </p>
              ) : mode === 'week' ? (
                <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
                  {groupedByDay.map(([date, entries]) => (
                    <div key={date}>
                      <div className="text-xs font-semibold text-gray-500 mb-1">{dayLabel(date)}</div>
                      <div className="grid grid-cols-3 gap-2">
                        {entries.map(({ s, i }) => renderSlot(s, i))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                  {info.slots.map((s, i) => renderSlot(s, i))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-gray-500">{chosenSlots.length} ausgewählt</span>
              <Button
                onClick={send}
                disabled={sending || chosenSlots.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="wa-reschedule-send"
              >
                {sending ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send className="mr-2" size={16} />}
                Angebot senden
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">Keine Daten.</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppRescheduleDialog;
